'use client';
import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import css from './HeroTitleReveal.module.css';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const DEFAULT_TITLE_REVEAL = {
  delay: 0.1,
  duration: 0.6,
  staggerAmount: 0.6,
  fromYPercent: 50,
  triggerStart: 'top 85%',
  markers: false,
  immediate: false,
};

/**
 * Split text content of a container into individual word <span> elements.
 */
const splitWords = container => {
  const words = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.textContent?.trim()) textNodes.push(node);
  }

  textNodes.forEach(textNode => {
    const text = textNode.textContent;
    const fragment = document.createDocumentFragment();

    text.split(/(\s+)/).forEach(part => {
      if (part.trim() === '') {
        fragment.appendChild(document.createTextNode(part));
      } else {
        const span = document.createElement('span');
        span.className = css.word;
        span.textContent = part;
        fragment.appendChild(span);
        words.push(span);
      }
    });

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  return words;
};

const isNearViewport = element => {
  const rect = element.getBoundingClientRect();
  const viewHeight = window.innerHeight || 800;
  // Include elements slightly above the fold (e.g. golden screen title at top: -103px)
  return rect.top < viewHeight && rect.bottom > -200;
};

const collectWords = element => {
  const existingWords = element.querySelectorAll(`.${css.word}`);
  return existingWords.length > 0 ? Array.from(existingWords) : splitWords(element);
};

/**
 * Word-by-word y/opacity reveal used by both the wrapper component and
 * selector-based CMS headings (e.g. #more-about-lamh-block-1 h4).
 */
const playTitleReveal = (element, options = {}) => {
  const {
    delay = DEFAULT_TITLE_REVEAL.delay,
    duration = DEFAULT_TITLE_REVEAL.duration,
    staggerAmount = DEFAULT_TITLE_REVEAL.staggerAmount,
    fromYPercent = DEFAULT_TITLE_REVEAL.fromYPercent,
    triggerStart = DEFAULT_TITLE_REVEAL.triggerStart,
    markers = DEFAULT_TITLE_REVEAL.markers,
    immediate = DEFAULT_TITLE_REVEAL.immediate,
  } = options;

  const words = collectWords(element);
  if (words.length === 0) return null;

  const playImmediately = immediate || isNearViewport(element);
  gsap.set(words, { yPercent: fromYPercent, opacity: 0 });

  const tweenVars = {
    yPercent: 0,
    opacity: 1,
    duration,
    ease: 'power3.out',
    stagger: { amount: staggerAmount },
    delay,
  };

  if (playImmediately) {
    return gsap.to(words, tweenVars);
  }

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: element,
      start: triggerStart,
      once: true,
      markers,
    },
  });
  tl.to(words, tweenVars);
  return tl;
};

const killTitleReveal = tween => {
  tween?.scrollTrigger?.kill();
  tween?.kill();
};

/**
 * Apply HeroTitleReveal to existing CMS nodes looked up by CSS selector
 * (e.g. "#more-about-lamh-block-1 h4"). Pass null to skip.
 */
export const useHeroTitleReveal = (selector, options = {}) => {
  const {
    delay = DEFAULT_TITLE_REVEAL.delay,
    duration = DEFAULT_TITLE_REVEAL.duration,
    staggerAmount = DEFAULT_TITLE_REVEAL.staggerAmount,
    fromYPercent = DEFAULT_TITLE_REVEAL.fromYPercent,
    triggerStart = DEFAULT_TITLE_REVEAL.triggerStart,
    markers = DEFAULT_TITLE_REVEAL.markers,
    immediate = DEFAULT_TITLE_REVEAL.immediate,
  } = options;

  useLayoutEffect(() => {
    if (!selector || typeof window === 'undefined') return;

    const elements = Array.from(document.querySelectorAll(selector));
    if (elements.length === 0) return;

    const sessions = elements.map(element => {
      const previousOverflow = element.style.overflow;
      element.style.overflow = 'hidden';
      return {
        element,
        previousOverflow,
        tween: playTitleReveal(element, {
          delay,
          duration,
          staggerAmount,
          fromYPercent,
          triggerStart,
          markers,
          immediate,
        }),
      };
    });

    return () => {
      sessions.forEach(({ element, previousOverflow, tween }) => {
        killTitleReveal(tween);
        element.style.overflow = previousOverflow;
      });
    };
  }, [selector, delay, duration, staggerAmount, fromYPercent, triggerStart, markers, immediate]);
};

/**
 * Animates children text word-by-word.
 * Use `immediate` for hero content that should play on mount (no scroll).
 */
export const HeroTitleReveal = ({
  children,
  className,
  delay = DEFAULT_TITLE_REVEAL.delay,
  duration = DEFAULT_TITLE_REVEAL.duration,
  staggerAmount = DEFAULT_TITLE_REVEAL.staggerAmount,
  fromYPercent = DEFAULT_TITLE_REVEAL.fromYPercent,
  triggerStart = DEFAULT_TITLE_REVEAL.triggerStart,
  markers = DEFAULT_TITLE_REVEAL.markers,
  immediate = DEFAULT_TITLE_REVEAL.immediate,
}) => {
  const wrapperRef = useRef(null);
  const tweenRef = useRef(null);

  useLayoutEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    killTitleReveal(tweenRef.current);
    tweenRef.current = playTitleReveal(element, {
      delay,
      duration,
      staggerAmount,
      fromYPercent,
      triggerStart,
      markers,
      immediate,
    });

    return () => {
      killTitleReveal(tweenRef.current);
    };
  }, [delay, duration, staggerAmount, fromYPercent, triggerStart, markers, immediate]);

  return (
    <div ref={wrapperRef} className={classNames(css.root, className)}>
      {children}
    </div>
  );
};

/**
 * Animates children as a block.
 * Use `immediate` for hero content that should play on mount (no scroll).
 */
export const HeroInfoReveal = ({
  children,
  fromYPercent = 50,
  duration = 1.4,
  staggerAmount = 0.6,
  delay = 0.1,
  markers = false,
  triggerStart = 'top 85%',
  triggerEnd = 'top 60%',
  scrub = false,
  immediate = false,
}) => {
  const wrapperRef = useRef(null);
  const tlRef = useRef(null);

  useLayoutEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    tlRef.current?.scrollTrigger?.kill();
    tlRef.current?.kill();

    const playImmediately = immediate || isNearViewport(element);

    gsap.set(element, { yPercent: fromYPercent, opacity: 0 });

    if (playImmediately) {
      tlRef.current = gsap.to(element, {
        yPercent: 0,
        opacity: 1,
        duration,
        ease: 'power4.out',
        delay,
      });
    } else {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: triggerStart,
          end: triggerEnd,
          scrub,
          once: true,
          markers,
        },
      });

      tl.to(element, {
        yPercent: 0,
        opacity: 1,
        duration,
        ease: 'power4.out',
        delay,
      });

      tlRef.current = tl;
    }

    return () => {
      tlRef.current?.scrollTrigger?.kill();
      tlRef.current?.kill();
    };
  }, [fromYPercent, duration, delay, markers, triggerStart, triggerEnd, scrub, immediate]);

  return (
    <div ref={wrapperRef} style={{ zIndex: 3, pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto' }}>{children}</div>
    </div>
  );
};

/**
 * Animates children sliding in from the X axis on scroll.
 */
export const HeroStatsReveal = ({
  children,
  y = 50,
  duration = 1.2,
  delay = 0.8,
  scrub = false,
  marker = false,
  start = '0 70%',
  end = '0 25%',
  staggerAmount = 0.1,
}) => {
  const statsRef = useRef(null);
  const tlRef = useRef(null);

  useLayoutEffect(() => {
    const element = statsRef.current;
    if (!element) return;

    tlRef.current?.scrollTrigger?.kill();
    tlRef.current?.kill();

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start,
        end,
        scrub,
        markers: marker,
      },
    });

    tl.from(element, {
      yPercent: y,
      opacity: 0,
      duration,
      ease: 'power4.out',
      delay,
      stagger: staggerAmount > 0 ? { amount: staggerAmount } : 0,
    });

    tlRef.current = tl;

    return () => {
      tlRef.current?.scrollTrigger?.kill();
      tlRef.current?.kill();
    };
  }, [y, duration, staggerAmount, delay, marker, start, end, scrub]);

  return <div ref={statsRef}>{children}</div>;
};